import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import { useToast } from '../../../src/components/Toast';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight } from '../../../src/utils/theme';
import { config } from '../../../src/config';

import PageFooter from '../../../src/components/PageFooter';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
  loadStripe,
} from '../../../src/stripe/webElements';

interface InvoiceSummary {
  invoiceNumber: string;
  companyName: string;
  businessName: string;
  totalAmount: number;
  dueDate: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  publishableKey: string;
  stripeAccountId: string;
  /** Account the PaymentIntent lives on: the owner's connected account for
   *  direct charges, or null for legacy platform destination-charge PIs. */
  chargeAccountId: string | null;
  clientSecret: string;
  paymentIntentId: string;
}

export default function PublicFleetInvoicePayScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();

  const params = useLocalSearchParams<{ id: string; token?: string }>();
  const id = params.id;
  // Web: also read ?token= from query string (expo-router on web only forwards path params reliably)
  const token = params.token ?? (typeof window !== 'undefined' && window.location ? new URLSearchParams(window.location.search).get('token') ?? '' : '');

  const [summary, setSummary] = useState<InvoiceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const [stripePromise, setStripePromise] = useState<any>(null);

  const load = useCallback(async () => {
    if (!id || !token) {
      setError('Invalid payment link.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${config.apiUrl}/api/public/fleet-invoices/${id}/summary?token=${encodeURIComponent(token)}`);
      if (res.status === 410) {
        const data = await res.json().catch(() => ({}));
        if (data.status === 'paid') {
          setPaid(true);
        } else {
          setError(data.error || 'Payment link expired.');
        }
        return;
      }
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        if (data.status === 'paid') {
          setPaid(true);
          return;
        }
        setError(data.error || 'This invoice can no longer be paid online.');
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to load payment summary (${res.status})`);
      }
      const data: InvoiceSummary = await res.json();
      setSummary(data);
      if (loadStripe && Platform.OS === 'web') {
        // Stripe.js must be scoped to the account the PaymentIntent lives on,
        // or the client secret resolves to "No such payment_intent" and the
        // PaymentElement never mounts (eternal spinner, disabled Pay button).
        // chargeAccountId = owner's connected account (direct charge); null =
        // legacy destination-charge PI on the platform account.
        setStripePromise(data.chargeAccountId
          ? loadStripe(data.publishableKey, { stripeAccount: data.chargeAccountId })
          : loadStripe(data.publishableKey));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load payment summary.');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  if (loading) {
    return <View style={styles.centerContainer}><ActivityIndicator size="large" color={colors.primary} /></View>;
  }
  if (paid) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="checkmark-circle" size={72} color={colors.success} />
        <Text style={styles.title}>Invoice Paid</Text>
        <Text style={styles.subtitle}>Thank you. The invoice is marked paid.</Text>
      </View>
    );
  }
  if (error) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="alert-circle" size={72} color={colors.danger} />
        <Text style={styles.title}>Payment Unavailable</Text>
        <Text style={styles.subtitle}>{error}</Text>
      </View>
    );
  }
  if (!summary) return null;

  if (Platform.OS !== 'web' || !Elements || !stripePromise) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="information-circle" size={48} color={colors.textLight} />
        <Text style={styles.title}>Open in your browser</Text>
        <Text style={styles.subtitle}>This payment link is best opened in your phone&apos;s browser to use Apple Pay, Google Pay, or a saved card.</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} keyboardShouldPersistTaps="handled">
      <View style={styles.brandRow}>
        <Text style={styles.brand}>RinsePay</Text>
      </View>
      <Card style={styles.summaryCard}>
        <Text style={styles.label}>Pay Invoice</Text>
        <Text style={styles.invoiceNumber}>#{summary.invoiceNumber}</Text>
        <Text style={styles.company}>{summary.companyName} → {summary.businessName}</Text>
        <Text style={styles.amount}>{fmt(summary.totalAmount)}</Text>
        <Text style={styles.due}>Due {new Date(summary.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
      </Card>

      <Card>
        <Elements stripe={stripePromise} options={{ clientSecret: summary.clientSecret, appearance: { theme: 'stripe' } }}>
          <PayForm
            summary={summary}
            token={token}
            onSuccess={() => setPaid(true)}
            colors={colors}
            styles={styles}
          />
        </Elements>
      </Card>
      <View style={{ height: spacing.xxl }} />
      <PageFooter />
    </ScrollView>
  );
}

interface PayFormProps {
  summary: InvoiceSummary;
  token: string;
  onSuccess: () => void;
  colors: ColorTheme;
  styles: ReturnType<typeof createStyles>;
}

function PayForm({ summary, token, onSuccess, colors, styles }: PayFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (confirmError) {
        toast.show({ type: 'error', title: 'Payment failed', message: confirmError.message });
        return;
      }
      if (paymentIntent?.status !== 'succeeded') {
        toast.show({ type: 'error', title: 'Payment failed', message: `Status: ${paymentIntent?.status}` });
        return;
      }
      // The invoice id is in the URL path: /pay/fleet-invoice/{id}
      const invoiceId = typeof window !== 'undefined' && window.location
        ? window.location.pathname.split('/').filter(Boolean).pop() ?? ''
        : '';
      const finalize = await fetch(`${config.apiUrl}/api/public/fleet-invoices/${invoiceId}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, paymentIntentId: paymentIntent.id }),
      });
      if (!finalize.ok) {
        // Even if our finalize call fails, the webhook will catch it. Still show success.
        const err = await finalize.json().catch(() => ({}));
        console.warn('Finalize call failed but PaymentIntent succeeded:', err);
      }
      onSuccess();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <View>
      <PaymentElement
        onReady={() => setReady(true)}
        options={{ layout: 'tabs', paymentMethodOrder: ['apple_pay', 'google_pay', 'card'] }}
      />
      {!ready && <View style={{ paddingVertical: spacing.md }}><ActivityIndicator color={colors.primary} /></View>}
      <Button
        title={processing ? 'Processing...' : `Pay $${(summary.totalAmount / 100).toFixed(2)}`}
        onPress={handleSubmit}
        loading={processing}
        disabled={!ready || processing}
        size="lg"
        style={{ marginTop: spacing.lg }}
      />
      <Text style={styles.footnote}>
        Secure payment via Stripe. Pays {summary.businessName} less Rinsepay&apos;s 1% platform fee.
      </Text>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, backgroundColor: c.background },

  brandRow: { alignItems: 'center', marginBottom: spacing.lg, marginTop: spacing.lg },
  brand: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.primary },

  summaryCard: { alignItems: 'center', paddingVertical: spacing.xl, marginBottom: spacing.lg },
  label: { fontSize: fontSize.xs, color: c.textSecondary, textTransform: 'uppercase', letterSpacing: 1 },
  invoiceNumber: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.xs },
  company: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 4 },
  amount: { fontSize: fontSize.hero, fontWeight: fontWeight.bold, color: c.primary, marginTop: spacing.md },
  due: { fontSize: fontSize.sm, color: c.warning, marginTop: spacing.xs },

  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.lg, textAlign: 'center' },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.sm, textAlign: 'center', maxWidth: 360 },
  footnote: { fontSize: fontSize.xs, color: c.textSecondary, textAlign: 'center', marginTop: spacing.md, maxWidth: 320, alignSelf: 'center' },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
